import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Surface, Button, Alert, Input, Tabs } from '../../ui/components';
import { FileText, Upload, MessageCircle, Eye, EyeOff, ArrowRight, Lock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useEarlyAccess } from '../../components/common/EarlyAccessProvider';

const BorrowerLoginPage = () => {
  const navigate = useNavigate();
  const { startApplication } = useEarlyAccess();
  const [searchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if already logged in
    checkSession();
  }, []);

  const checkSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const redirect = searchParams.get('redirect') || '/borrower/dashboard';
      navigate(redirect);
    }
  };

  const handleDemoLogin = () => {
    // Demo mode - bypass authentication
    const demoUser = {
      id: 'demo-borrower-123',
      email: 'demo@borrower.com',
      user_metadata: {
        firstName: 'Demo',
        lastName: 'Borrower',
      },
    };

    // Store demo session
    localStorage.setItem('lk_demo_session', JSON.stringify(demoUser));
    
    const redirect = searchParams.get('redirect') || '/borrower/dashboard';
    navigate(redirect);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.session) {
        const redirect = searchParams.get('redirect') || '/borrower/dashboard';
        navigate(redirect);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            firstName,
            lastName,
          },
        },
      });

      if (error) throw error;

      if (data.user) {
        setSuccess('Account created successfully! Please check your email to verify your account.');
        // Auto-login after signup
        setTimeout(() => {
          const redirect = searchParams.get('redirect') || '/borrower/dashboard';
          navigate(redirect);
        }, 2000);
      }
    } catch (error) {
      console.error('Signup error:', error);
      setError(error.message || 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  const FeatureRow = ({ icon: Icon, title, desc }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-3)', padding: 'var(--lk-space-3)', borderRadius: 'var(--lk-radius-md)', background: 'rgba(255,255,255,0.1)' }}>
      <div style={{ width: 44, height: 44, borderRadius: 'var(--lk-radius-md)', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={24} />
      </div>
      <div style={{ textAlign: 'left' }}>
        <div style={{ fontWeight: 600, fontSize: 'var(--lk-text-sm)' }}>{title}</div>
        <div style={{ fontSize: 'var(--lk-text-xs)', opacity: 0.8 }}>{desc}</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
      {/* Left Side */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 'var(--lk-space-10)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -100, right: -100, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <div style={{ position: 'absolute', bottom: -50, left: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.02)' }} />
        <div style={{ maxWidth: 500, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <div style={{ marginBottom: 'var(--lk-space-6)', display: 'flex', justifyContent: 'center' }}>
            <img src="/assets/loanknow-logo-white.svg" alt="LoanKnow" style={{ height: 48, display: 'block' }} />
          </div>
          <h1 style={{ fontSize: 'var(--lk-text-4xl)', fontWeight: 700, marginBottom: 'var(--lk-space-3)', letterSpacing: '-0.02em' }}>
            Your Mortgage Hub
          </h1>
          <p style={{ fontSize: 'var(--lk-text-lg)', opacity: 0.9, marginBottom: 'var(--lk-space-8)' }}>
            Track your application, upload documents, and forward your file to your loan officer or realtor.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
            <FeatureRow icon={FileText} title="Track Your Application" desc="Real-time status updates" />
            <FeatureRow icon={Upload} title="Upload Documents" desc="Secure document management" />
            <FeatureRow icon={MessageCircle} title="Owen, Your AI Guide" desc="Get answers any time, day or night" />
          </div>
        </div>
      </div>

      {/* Right Side */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 'var(--lk-space-10)',
        background: 'white',
      }}>
        <div style={{ width: '100%', maxWidth: 440 }}>
          <div style={{ textAlign: 'center', marginBottom: 'var(--lk-space-6)' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img
                src="/assets/loanknow-logo.svg"
                alt="LoanKnow"
                style={{ height: 48, display: 'block', marginBottom: 'var(--lk-space-3)' }}
              />
            </div>
            <h2 style={{ fontWeight: 700, marginBottom: 4 }}>Borrower Portal</h2>
            <p style={{ color: 'var(--lk-text-tertiary)', margin: 0 }}>Access your loan application and track your progress</p>
          </div>

          <Tabs
            tabs={[
              { id: 0, label: 'Sign In' },
              { id: 1, label: 'Create Account' },
            ]}
            activeTab={tabValue}
            onChange={setTabValue}
            className=""
          />

          <div style={{ marginTop: 'var(--lk-space-4)' }}>
            {error && (
              <Alert variant="error" style={{ marginBottom: 'var(--lk-space-3)' }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert variant="success" style={{ marginBottom: 'var(--lk-space-3)' }}>
                {success}
              </Alert>
            )}

            {tabValue === 0 && (
              <form onSubmit={handleLogin}>
                <Alert variant="info" style={{ marginBottom: 'var(--lk-space-3)' }}>
                  <strong>Demo Mode Available</strong>
                  <div style={{ fontSize: 'var(--lk-text-xs)' }}>Click "Demo Login" below to explore without creating an account</div>
                </Alert>

                <Input
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
                <div style={{ marginTop: 'var(--lk-space-3)', position: 'relative' }}>
                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: 10, top: 34, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--lk-text-tertiary)', padding: 4 }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={isLoading}
                  style={{ width: '100%', marginTop: 'var(--lk-space-4)' }}
                >
                  Sign In
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  icon={Eye}
                  onClick={handleDemoLogin}
                  style={{ width: '100%', marginTop: 'var(--lk-space-2)' }}
                >
                  Demo Login (No Account Required)
                </Button>

                <div style={{ textAlign: 'center', marginTop: 'var(--lk-space-3)' }}>
                  <a href="#" style={{ color: 'var(--lk-primary-500)', fontSize: 'var(--lk-text-sm)', textDecoration: 'none' }}>
                    Forgot password?
                  </a>
                </div>
              </form>
            )}

            {tabValue === 1 && (
              <form onSubmit={handleSignup}>
                <Input
                  label="First Name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  autoComplete="given-name"
                  required
                />
                <Input
                  label="Last Name"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  autoComplete="family-name"
                  required
                  style={{ marginTop: 'var(--lk-space-3)' }}
                />
                <Input
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                  style={{ marginTop: 'var(--lk-space-3)' }}
                />
                <div style={{ marginTop: 'var(--lk-space-3)', position: 'relative' }}>
                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: 10, top: 34, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--lk-text-tertiary)', padding: 4 }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)', marginTop: 4 }}>At least 6 characters</div>

                <Input
                  label="Confirm Password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                  required
                  style={{ marginTop: 'var(--lk-space-3)' }}
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={isLoading}
                  style={{ width: '100%', marginTop: 'var(--lk-space-4)' }}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </Button>

                <p style={{ color: 'var(--lk-text-quaternary)', textAlign: 'center', fontSize: 'var(--lk-text-xs)', marginTop: 'var(--lk-space-3)' }}>
                  By creating an account, you agree to our Terms of Service and Privacy Policy
                </p>
              </form>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-3)', margin: 'var(--lk-space-5) 0' }}>
            <hr style={{ flex: 1, border: 'none', borderTop: '1px solid var(--lk-border-subtle)' }} />
            <span style={{ color: 'var(--lk-text-quaternary)', fontSize: 'var(--lk-text-xs)' }}>OR</span>
            <hr style={{ flex: 1, border: 'none', borderTop: '1px solid var(--lk-border-subtle)' }} />
          </div>

          <Button
            variant="secondary"
            style={{ width: '100%' }}
            onClick={() => startApplication('borrower_login_cta')}
          >
            Start New Application
          </Button>

          <div style={{ textAlign: 'center', marginTop: 'var(--lk-space-4)' }}>
            <a href="/" style={{ color: 'var(--lk-text-quaternary)', fontSize: 'var(--lk-text-sm)', textDecoration: 'none' }}>
              ← Back to Home
            </a>
          </div>

          <p style={{ color: 'var(--lk-text-quaternary)', textAlign: 'center', fontSize: 'var(--lk-text-xs)', marginTop: 'var(--lk-space-4)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <Lock size={12} />
            Secure login • 256-bit SSL encryption
          </p>
        </div>
      </div>
    </div>
  );
};

export default BorrowerLoginPage;
